// "Security": what protects a capsule today, and what does not (D-3 A).
// Sources: apps/cli/encryption.py (ML-KEM-768 + AES-256-GCM envelope, ALG_ID, PBKDF2 600 000),
// apps/cli/collect.py (keys decrypt-capsule verifies SHA-256; LLM keys from env),
// apps/web/src/lib/protection.ts (honest labels), apps/web/src/lib/api.ts
// (getCapsulePayload: gunzip + SHA-256 check), synapse-connect/backend/src/routes/
// collectCapsuleRoutes.ts (workspace-bound access, 404 outside it), apps/web/README.md +
// src/lib/oidc.ts + api/_lib/trust.ts (Ordinal Pass: OIDC code + PKCE, off by default).
import { motion } from "motion/react";
import { Lock, Tag, Fingerprint, FolderLock, KeyRound, Cpu } from "lucide-react";
import { useLandingTheme } from "./theme";

export function SecuritySection({ passSignIn }: { passSignIn: boolean }) {
  const { tone } = useLandingTheme();
  const items = [
    {
      icon: Lock,
      title: "Post-quantum encryption — CLI only",
      tag: "Experimental",
      body: (
        <>
          Experimental, in the CLI: <span className="font-mono text-[0.92em]">send-to-hive --encrypt</span> seals the
          capsule with ML-KEM-768 (FIPS 203) and AES-256-GCM. The library behind it is not yet a vetted,
          constant-time implementation, so do not rely on it alone for sensitive data. The workspace's private key
          stays in the workspace folder, encrypted with your passphrase (PBKDF2, 600,000 iterations); lose either and
          the capsule cannot be opened. The server stores the sealed envelope, not your text — only the workspace
          name, file names, size and hash travel unencrypted. Decrypt locally with <span className="font-mono text-[0.92em]">keys decrypt-capsule</span>.
        </>
      ),
    },
    {
      icon: Tag,
      title: "Labelled honestly",
      body: (
        <>
          Capsules from the web app and the extension are gzip-compressed and SHA-256 hashed — not encrypted —
          and the app labels them <em>compressed</em>. Only CLI capsules sent with --encrypt (experimental) are
          labelled <em>encrypted</em>.
        </>
      ),
    },
    {
      icon: Fingerprint,
      title: "Checked when opened",
      body: <>Opening a capsule in the web app decompresses its payload and compares the SHA-256 with the hash recorded when it was sent; the CLI does the same when it decrypts an encrypted capsule.</>,
    },
    {
      icon: FolderLock,
      title: "Your workspaces only",
      body: <>An access key is bound to its workspaces. A capsule outside them answers as if it did not exist.</>,
    },
    {
      icon: KeyRound,
      title: passSignIn ? "Ordinal Pass sign-in" : "Ordinal Pass sign-in — planned",
      body: passSignIn ? (
        <>You sign in at Qodoonect Trust (OpenID Connect with PKCE). Collect gets a short-lived token for your workspace, kept in memory — never your password.</>
      ) : (
        <>Built and switched off for now: sign-in with your Ordinal Pass at Qodoonect Trust (OpenID Connect with PKCE). Until then the closed beta uses access keys.</>
      ),
    },
    {
      icon: Cpu,
      title: "Your LLM, your key",
      body: <>The CLI reads your Anthropic, OpenAI or OpenRouter key from the environment; LLM requests go to that provider only.</>,
    },
  ];
  return (
    <section id="security" aria-labelledby="security-title" className="px-6 md:px-12 py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        <header className="mb-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Security</p>
          <h2 id="security-title" className="text-5xl md:text-7xl font-serif font-light leading-[0.95]">
            Hashed and checked.<br />
            <span className="italic text-white/65">Honest everywhere.</span>
          </h2>
        </header>

        <motion.ul
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-panel-strong rounded-3xl p-7 md:p-10 grid gap-x-12 gap-y-10 md:grid-cols-2"
        >
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <li key={it.title} className="flex gap-4">
                <Icon size={20} strokeWidth={1.6} className="flex-none mt-1" style={{ color: tone("#34D399") }} aria-hidden="true" />
                <div className="min-w-0">
                  <h3 className="text-lg font-medium mb-2">
                    {it.title}
                    {it.tag && (
                      <span className="collect-tag ml-2 align-middle" style={{ color: tone("#F2C94C"), borderColor: "#F2C94C55" }}>{it.tag}</span>
                    )}
                  </h3>
                  <p className="text-sm font-light text-white/70 leading-relaxed">{it.body}</p>
                </div>
              </li>
            );
          })}
        </motion.ul>
      </div>
    </section>
  );
}
